import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";

const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("/api/files/")) return `${BACKEND_URL}${url}`;
  return url;
};

export default function Journal() {
  const [posts, setPosts] = useState(null);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    document.title = "The Journal — Ram Shack";
    api
      .get("/journal")
      .then((r) => setPosts(r.data))
      .catch(() => setPosts([]));
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribing(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email: email.trim() });
      setSubscribed(true);
      setEmail("");
      toast.success(
        data.message === "Already on the list"
          ? "You're already on the list."
          : "Welcome to memoirs — a letter is on its way.",
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Could not subscribe — check the address");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div data-testid="journal-page" className="px-6 md:px-12 lg:px-16 py-16 md:py-24">
      <div className="overline mb-4">Letters, rituals, confessions</div>
      <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-bone mb-16 leading-tight">
        The Journal.
      </h1>

      {posts === null && <div className="text-muted-bone">Loading…</div>}

      {posts?.length === 0 && (
        <div
          className="border p-16 text-center text-muted-bone"
          style={{ background: "var(--rs-surface)", borderColor: "var(--rs-border)" }}
        >
          The house has not yet put pen to paper. Return soon.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {posts?.map((post) => (
          <Link
            key={post.post_id}
            data-testid={`journal-card-${post.slug}`}
            to={`/journal/${post.slug}`}
            className="group border flex flex-col transition hover:border-opacity-60"
            style={{ background: "var(--rs-surface)", borderColor: "var(--rs-border)" }}
          >
            {post.hero_image && (
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={resolveImg(post.hero_image)}
                  alt={post.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            )}
            <div className="p-8 flex flex-col gap-3 flex-1">
              <div className="overline">
                {post.published_at?.slice(0, 10)} · {post.author || "The House"}
              </div>
              <h2 className="font-serif-display text-lg md:text-lg text-bone leading-snug group-hover:underline underline-offset-4 decoration-1">
                {post.title}
              </h2>
              <p className="text-muted-bone text-sm leading-relaxed">{post.excerpt}</p>
              <div className="mt-auto pt-4 flex flex-wrap gap-2">
                {(post.tags || []).slice(0, 3).map((t) => (
                  <span
                    key={t}
                    className="text-[10px] uppercase tracking-[0.2em] text-muted-bone border px-2 py-1"
                    style={{ borderColor: "var(--rs-border)" }}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Monthly Memoir — velvet subscribe card, gothic gilt trim */}
      <section
        data-testid="journal-newsletter"
        className="relative mt-24 md:mt-32 max-w-3xl mx-auto"
      >
        <div className="rs-memoir-card relative px-8 md:px-14 py-14 md:py-20 text-center overflow-hidden">
          {/* velvet base */}
          <div aria-hidden className="rs-memoir-velvet" />
          {/* brass gilt frame */}
          <div aria-hidden className="rs-memoir-brass" />
          {/* candlelight bloom */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at 50% 0%, rgba(220,150,80,0.14) 0%, rgba(0,0,0,0) 55%)",
            }}
          />

          <div className="relative z-10">
            <div className="overline mb-4" style={{ color: "#d4af37" }}>
              A private correspondence
            </div>
            <h2 className="font-serif-display text-3xl md:text-5xl text-bone leading-tight mb-5">
              The Monthly Memoir.
            </h2>
            <p className="text-muted-bone text-sm md:text-base leading-relaxed max-w-xl mx-auto mb-8">
              One quiet letter on the first of every month — a ritual, a
              recommendation, and something the house has been thinking about.
              Sealed. Discreet. Unsubscribe with a single tap.
            </p>

            {subscribed ? (
              <div
                data-testid="journal-newsletter-success"
                className="inline-block px-8 py-4 uppercase tracking-[0.28em] text-xs"
                style={{
                  color: "#d4af37",
                  border: "1px solid rgba(212,175,55,0.55)",
                  background: "rgba(60,10,15,0.4)",
                }}
              >
                The house will write to you soon.
              </div>
            ) : (
              <form
                data-testid="journal-newsletter-form"
                onSubmit={subscribe}
                className="max-w-lg mx-auto"
              >
                <div
                  className="flex"
                  style={{ border: "1px solid rgba(212,175,55,0.55)" }}
                >
                  <input
                    data-testid="journal-newsletter-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@address.com"
                    required
                    className="flex-1 bg-transparent px-4 py-4 text-sm text-bone placeholder:text-muted-bone outline-none"
                  />
                  <button
                    data-testid="journal-newsletter-submit"
                    type="submit"
                    disabled={subscribing || !email.trim()}
                    className="rs-memoir-seal px-6"
                  >
                    {subscribing ? "…" : "Seal my letter"}
                  </button>
                </div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-muted-bone/70 mt-4">
                  No spam. No sharing. One letter per month.
                </div>
              </form>
            )}
          </div>
        </div>

        <style>{`
          .rs-memoir-card {
            background:
              radial-gradient(ellipse at 20% 0%, rgba(70,20,20,0.55) 0%, rgba(0,0,0,0) 55%),
              linear-gradient(180deg, #1a0d0d 0%, #0c0606 100%);
            box-shadow:
              0 30px 80px rgba(0,0,0,0.55),
              inset 0 0 90px rgba(0,0,0,0.55);
          }
          .rs-memoir-velvet {
            position: absolute;
            inset: 10px;
            pointer-events: none;
            z-index: 0;
            background:
              repeating-linear-gradient(
                90deg,
                rgba(80,15,20,0.55) 0px,
                rgba(55,10,15,0.55) 3px,
                rgba(75,12,18,0.55) 6px,
                rgba(50,8,12,0.55) 9px
              ),
              radial-gradient(ellipse at 50% 0%, rgba(130,25,35,0.35) 0%, rgba(0,0,0,0) 60%),
              linear-gradient(180deg, #4a0f18 0%, #1e0508 100%);
            box-shadow:
              inset 0 0 60px rgba(0,0,0,0.65),
              inset 0 0 0 1px rgba(0,0,0,0.6);
          }
          .rs-memoir-brass {
            position: absolute;
            inset: 4px;
            pointer-events: none;
            z-index: 1;
            border: 1px solid rgba(212,175,55,0.55);
            box-shadow:
              inset 0 0 0 1px rgba(0,0,0,0.55),
              inset 0 0 14px rgba(212,175,55,0.14);
          }
          .rs-memoir-seal {
            color: #f5f2eb;
            font-family: inherit;
            text-transform: uppercase;
            letter-spacing: 0.28em;
            font-size: 11px;
            border: none;
            cursor: pointer;
            background:
              radial-gradient(circle at 35% 30%, #c74c3d 0%, #8a1c1c 45%, #3a0808 100%);
            box-shadow:
              inset 0 0 0 1px rgba(212,175,55,0.55),
              inset 0 6px 18px rgba(255,180,140,0.18),
              inset 0 -6px 18px rgba(0,0,0,0.6);
            transition: transform 200ms ease-out, box-shadow 200ms ease-out;
          }
          .rs-memoir-seal:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow:
              inset 0 0 0 1px rgba(230,190,80,0.75),
              inset 0 8px 20px rgba(255,200,160,0.24),
              inset 0 -8px 20px rgba(0,0,0,0.65);
          }
          .rs-memoir-seal:disabled { opacity: 0.5; cursor: not-allowed; }
        `}</style>
      </section>
    </div>
  );
}
